import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";
import {
  Chart, LineController, LineElement, PointElement,
  LinearScale, CategoryScale, Tooltip, Filler,
} from "chart.js";

// Filler is REQUIRED in Chart.js 4 for `fill: true` to render the subtle area
// under the line (the existing event-graph controller omitted it; the fill was a
// no-op there). Legend is intentionally NOT registered/shown — this is a
// single-series chart and the metric switcher labels it.
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

// Connects to data-controller="reports-trend"
//
// The Reports sales-trend chart. A SINGLE-series Chart.js line/area, driven by a
// metric switcher (Revenue · Orders · Tickets — one at a time, never 3 on one
// axis). Range-aware: fetches {dates,revenue,orders,tickets,non_zero_days,
// has_data} from the reports #stats endpoint for the page's active range, so the
// chart agrees with the tiles/table. Manages loading / low-volume / no-data-in-
// range states and mirrors the series into a visually-hidden table for a11y + e2e.
export default class extends Controller {
  static targets = [
    "canvas", "loading", "lowVolume", "empty", "body",
    "metricBtn", "fallbackCaption", "fallbackHead", "fallbackBody",
  ];
  static values = {
    url: String,            // stats endpoint
    range: String,          // active preset (7d/30d/90d/year/all/custom)
    startDate: String,      // for custom ranges
    endDate: String,
    metric: { type: String, default: "revenue" },
    // Below this many non-zero days, show the low-volume banner + point markers.
    lowVolumeThreshold: { type: Number, default: 5 },
    // i18n labels passed from the view (no hardcoded English here).
    revenueLabel: String,
    ordersLabel: String,
    ticketsLabel: String,
  };

  // Brand palette per metric (Revenue = brand blue area, matching the style guide
  // demo; Orders/Tickets reuse DS accent colors). [line, fill].
  METRIC_STYLES = {
    revenue: ["#2684FF", "rgba(38,132,255,.10)"],
    orders: ["#1868DB", "rgba(24,104,219,.10)"],
    tickets: ["#B47B00", "rgba(180,123,0,.10)"],
  };

  connect() {
    // NB: Stimulus's base Controller exposes a read-only `data` getter (the
    // dataset API), so the fetched payload is stored on `statsData`, not `data`.
    this.metric = this.metricValue;
    this.statsData = null;
    this.chart = null;
    this.fetchData();
  }

  disconnect() {
    if (this.chart) { this.chart.destroy(); this.chart = null; }
  }

  metricLabel(metric) {
    if (metric === "orders") return this.ordersLabelValue;
    if (metric === "tickets") return this.ticketsLabelValue;
    return this.revenueLabelValue;
  }

  // Metric switcher click — swap the active series without a network round-trip
  // (we already have all three arrays from the single fetch).
  switchMetric(event) {
    const btn = event.currentTarget;
    const metric = btn.dataset.metric;
    if (!metric || metric === this.metric) return;
    this.metric = metric;

    this.metricBtnTargets.forEach((b) => {
      const on = b.dataset.metric === metric;
      b.classList.toggle("on", on);
      b.setAttribute("aria-selected", on.toString());
    });

    if (this.statsData) this.render();
  }

  async fetchData() {
    this.showLoading();
    try {
      const response = await get(this.urlValue, {
        query: {
          range: this.rangeValue,
          start_date: this.startDateValue,
          end_date: this.endDateValue,
        },
        responseKind: "json",
      });
      if (!response.ok) throw new Error(`stats ${response.statusCode}`);
      this.statsData = await response.json;
      this.render();
    } catch (e) {
      // Fail soft: hide the skeleton and leave the card frame (no broken axis).
      console.error("reports-trend: failed to load chart data", e);
      this.hideLoading();
    }
  }

  render() {
    const data = this.statsData;
    if (!data) return;

    // No-data-in-range: org has orders elsewhere, none in this window. Show the
    // calm in-range empty (distinct from the page's true-empty), no chart.
    if (!data.has_data) {
      this.showEmpty();
      this.populateFallback(data);
      return;
    }
    this.showChart();

    // Low-volume: a few non-zero days over the range → quiet banner + visible
    // point markers (so the sparse reality reads honestly, not a fake curve).
    const sparse = data.non_zero_days > 0 && data.non_zero_days < this.lowVolumeThresholdValue;
    if (this.hasLowVolumeTarget) this.lowVolumeTarget.classList.toggle("d-none", !sparse);

    this.drawChart(data, sparse);
    this.populateFallback(data);
  }

  drawChart(data, sparse) {
    const series = data[this.metric] || [];
    const [line, fill] = this.METRIC_STYLES[this.metric] || this.METRIC_STYLES.revenue;
    const label = this.metricLabel(this.metric);
    const money = this.metric === "revenue";

    // Markers visible when sparse OR when the range itself is short (≤12 points),
    // hidden when dense — mirrors the style-guide demo's pointRadius rule.
    const pointRadius = sparse || series.length <= 12 ? 3 : 0;

    if (this.chart) this.chart.destroy();
    const ctx = this.canvasTarget.getContext("2d");
    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.dates,
        datasets: [{
          label,
          data: series,
          borderColor: line,
          backgroundColor: fill,
          borderWidth: 2,
          fill: true,           // subtle area (matches event-graph fill style)
          tension: 0.35,
          pointRadius,
          pointHoverRadius: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false }, // single series — switcher is the label
          tooltip: {
            backgroundColor: "rgba(255,255,255,.96)",
            titleColor: "#101828", bodyColor: "#475467",
            borderColor: "#e4e7ec", borderWidth: 1, padding: 10,
            callbacks: money ? {
              label: (c) => `${label}: ${this.formatMoney(c.parsed.y)}`,
            } : { label: (c) => `${label}: ${c.parsed.y}` },
          },
        },
        scales: {
          y: {
            beginAtZero: true,    // honest: a spike reads true against 0
            grid: { color: "rgba(16,24,40,.06)" },
            ticks: {
              color: "#525252",
              callback: money ? (v) => this.formatMoney(v) : (v) => v,
            },
          },
          x: { grid: { display: false }, ticks: { color: "#525252", maxTicksLimit: 8 } },
        },
      },
    });
  }

  formatMoney(v) {
    return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }

  // Visually-hidden data table mirroring the active series — the a11y fallback
  // and the e2e assertion surface (Playwright reads numbers, not the canvas).
  populateFallback(data) {
    if (!this.hasFallbackBodyTarget) return;
    const series = data[this.metric] || [];
    const label = this.metricLabel(this.metric);
    const money = this.metric === "revenue";

    if (this.hasFallbackCaptionTarget) this.fallbackCaptionTarget.textContent = label;
    if (this.hasFallbackHeadTarget) {
      this.fallbackHeadTarget.querySelector("[data-metric-col]").textContent = label;
    }

    const rows = data.dates.map((d, i) => {
      const val = money ? this.formatMoney(series[i]) : (series[i] ?? 0);
      return `<tr><td>${d}</td><td>${val}</td></tr>`;
    });
    this.fallbackBodyTarget.innerHTML = rows.join("");
  }

  // ── State toggles ──────────────────────────────────────────────────────────
  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("d-none");
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("d-none");
  }
  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("d-none");
  }
  showChart() {
    this.hideLoading();
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("d-none");
    if (this.hasCanvasTarget) this.canvasTarget.classList.remove("d-none");
  }
  showEmpty() {
    this.hideLoading();
    if (this.chart) { this.chart.destroy(); this.chart = null; }
    if (this.hasCanvasTarget) this.canvasTarget.classList.add("d-none");
    if (this.hasLowVolumeTarget) this.lowVolumeTarget.classList.add("d-none");
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("d-none");
  }
}
